import {
	type CommentaryLanguages,
	type CommentaryTemplateDto,
	type CustomReportDataDTO,
	type InvestmentCommentaryResponse,
	type InvestmentSummary,
	type PortfolioExAnteMetricTypeEnum,
	type PortfolioMetricPreferenceDTO,
	CommentaryTemplateControllerApiFactory,
	InstrumentsClassificationsControllerV1ApiFactory,
	InvestmentEnhancementReportsControllerApiFactory,
	InvestmentReportsControllerApiFactory,
	InvestmentsStaticConfigurationControllerApiFactory,
	PortfolioStudioPreferencesApiFactory,
} from "@eu.mdotm.api/sphere";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import type { MaybePromise } from "@mdotm/utils/promise";
import i18next from "i18next";
import { Map } from "immutable";
import { getApiGen } from "$root/api/factory";
import type { ComponentAndPropsPair } from "$root/components/EvolvedPrint/configuration/types";
import type {
	ReportTemplate,
	ReportTemplateItemFor,
	ReportTemplateItemMap,
	ReportTemplateStatus,
	ReportTemplateVariant,
} from "$root/functional-areas/reports/report-latest";
import type { Languages } from "$root/localization/i18n";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { customObjectEntriesFn, parallelize, structuredParallelize, typedObjectEntries } from "$root/utils";
import { type BondDividendRow, toBondDividendRow } from "$root/widgets-architecture/widgets/BondsDividendsTracker/data";
import { toCouponGroups, toMaturityGroups } from "$root/widgets-architecture/widgets/MaturityCouponTracker/data";
import { investmentCustomAttributesRepository } from "../customAttributes/repository";
import { createInstrumentList, reviewTickerToInstrumentsMap } from "../instruments/repository";
import { classificationMetricDisplay, decimalPlaces, unitOfMeasurement } from "../metrics";
import { portfolioRepository } from "../portfolio/repository";
import type { IUser } from "../user";
import { userRepository } from "../user/repository";
import BondsDividendsTracker from "./components/BondsDividendsTracker";
import Commentary, { getCommentaryProps } from "./components/Commentary";
import Composition from "./components/Composition";
import { Cover } from "./components/Cover";
import { Disclaimer } from "./components/Disclaimer";
import ExAnteMetrics from "./components/ExAnteMetrics";
import ExAnteVolatilityContribution, {
	type ExAnteVolatilityContributionSelectionProps,
	getExAnteVolatilityContributionSplittableProps,
} from "./components/ExAnteVolatilityContribution";
import {
	chunkExposureListItems,
	Exposure,
	exposureChartHeight,
	exposureEntriesToSplittableProps,
} from "./components/Exposure";
import { ExposureCompare, exposureCompareEntriesToSplittableProps } from "./components/ExposureCompare";
import ExposureFactors, { getExposureFactorsProps } from "./components/ExposureFactors";
import { FreeText } from "./components/FreeText";
import MaturityCouponTracker, { type MaturityCouponPrintRow } from "./components/MaturityCouponTracker";
import { PortfolioPerformance } from "./components/Performance";
import PerformanceAttribution from "./components/PerformanceAttribution";
import PerformanceVolatilityContribution, {
	type PerformanceContributionVolatilitySelectionProps,
	getPerformanceVolatilityContributionProps,
} from "./components/PerformanceVolatilityContribution";
import { PortfolioMetrics } from "./components/PortfolioMetrics";
import { Summary } from "./components/Summary";
import { jsxHelper } from "./utils";

const i18nLangToCommentaryLang: Record<Languages, CommentaryLanguages> = {
	it: "ITALIAN",
	en: "ENGLISH",
	de: "GERMAN",
	fr: "FRENCH",
	es: "SPANISH",
};

const commentaryLanguageToI18nLang: Record<CommentaryLanguages, Languages> = {
	ITALIAN: "it",
	ENGLISH: "en",
	FRENCH: "fr",
	GERMAN: "de",
	SPANISH: "es",
};

const volatilityList = ["VOLATILITY_1Y", "VOLATILITY_2Y", "VOLATILITY_3Y"] as const;
const varList = [
	"PARAMETRIC_VAR_95_1Y",
	"PARAMETRIC_VAR_975_1Y",
	"PARAMETRIC_VAR_99_1Y",
	"PARAMETRIC_VAR_95_2Y",
	"PARAMETRIC_VAR_975_2Y",
	"PARAMETRIC_VAR_99_2Y",
	"PARAMETRIC_VAR_95_3Y",
	"PARAMETRIC_VAR_975_3Y",
	"PARAMETRIC_VAR_99_3Y",
	"HISTORICAL_VAR_95_1Y",
	"HISTORICAL_VAR_975_1Y",
	"HISTORICAL_VAR_99_1Y",
	"HISTORICAL_VAR_95_2Y",
	"HISTORICAL_VAR_975_2Y",
	"HISTORICAL_VAR_99_2Y",
	"HISTORICAL_VAR_95_3Y",
	"HISTORICAL_VAR_975_3Y",
	"HISTORICAL_VAR_99_3Y",
] as const;
const trackingErrorList = ["TRACKING_ERROR_3Y"] as const;

export type CommonPortfolioData = {
	portfolio: InvestmentSummary;
	variant: ReportTemplateVariant;
	report: CustomReportDataDTO;
	language: Languages;
	commentaryTemplates: CommentaryTemplateDto[];
	/** Pinned date (`YYYY-MM-DD`) every provider reads its data at; unset = live. */
	asOf: string | undefined;
};

export async function getDataForTemplate(
	portfolioUid: string,
	reportTemplate: ReportTemplate,
	variant: ReportTemplateVariant,
	status: ReportTemplateStatus,
	user: IUser,
	asOfParam: string | undefined,
): Promise<{
	componentAndPropsList: Array<Promise<Array<ComponentAndPropsPair<any, any>>>>;
	common: CommonPortfolioData;
}> {
	// A proposal has no stored snapshot, and the backend skips its snapshot wait for proposal pages,
	// so half the providers would read the pinned past and half the live proposal. One report, one
	// portfolio: the date is dropped rather than mixed. An empty string is the URL's way of saying
	// "no date" and the server reads it as present, so it is normalised here too.
	const asOf = variant === "proposal" || !asOfParam ? undefined : asOfParam;

	const portfolioStudioPreferenceApi = getApiGen(PortfolioStudioPreferencesApiFactory);

	const { commentaryLanguage } = await axiosExtract(portfolioStudioPreferenceApi.getExplainabilitySettings());
	const common: CommonPortfolioData = {
		asOf,
		// The date is spread in only when it exists: an `asOf: undefined` appends an element to the
		// query key, which would change the live view's cache entry.
		portfolio: await portfolioRepository.summary.fetchQuery(portfolioUid, {
			enhanced: variant === "proposal",
			...(asOf ? { asOf } : {}),
		}),
		report: await userRepository.customReportData.fetchQuery(),
		variant,
		commentaryTemplates: await axiosExtract(getApiGen(CommentaryTemplateControllerApiFactory).getTemplateList()),
		language: commentaryLanguageToI18nLang[commentaryLanguage ?? "ENGLISH"],
	};

	const all = structuredParallelize(
		(status === "draft" ? reportTemplate.data.templateDraftItemsByVariant : reportTemplate.data.templateItemsByVariant)[
			variant
		].map((x, index) => () => getDataForItemTemplate(x, common, index, user)),
		{ concurrency: 10 },
	);
	return { common, componentAndPropsList: all };
}

function getDataForItemTemplate<K extends keyof ReportTemplateItemMap, T extends ReportTemplateItemFor<K>>(
	item: T,
	common: CommonPortfolioData,
	index: number,
	user: IUser,
): ReturnType<(typeof itemTemplateDataProviders)[K]> {
	// Cheating a bit here but it should be fine
	return (itemTemplateDataProviders[item.kind] as any)(item, common, index, user) as ReturnType<
		(typeof itemTemplateDataProviders)[K]
	>;
}

/** @internal — exported for tests */
export const itemTemplateDataProviders = {
	cover(config, common) {
		const { portfolio, report } = common;
		return [
			jsxHelper(Cover, {
				commonProps: {
					title: config.title ? config.title : "PORTFOLIO DETAILS",
					subtitle: `${portfolio.name?.toUpperCase()}`,
					backgroundImage: report.backgroundImage?.realData ? report.backgroundImage : undefined,
					clientLogo: report.clientLogo?.realData ? report.clientLogo : undefined,
				},
				splittableProps: [],
				hideFooter: true,
				hideHeader: true,
			}),
		];
	},
	async summary(config, common) {
		return [
			jsxHelper(Summary, {
				commonProps: {
					portfolio: common.portfolio,
					portfolioAttributes: (
						await axiosExtract(
							getApiGen(InvestmentsStaticConfigurationControllerApiFactory).getStaticConfiguration(
								common.portfolio.uuid!,
							),
						)
					).mainInfo?.customAttributes,
					availableAttributes: await investmentCustomAttributesRepository.customAttributes.fetchQuery(),
					config,
				},
				splittableProps: [],
			}),
		];
	},
	async portfolioMetrics(config, common) {
		const { preferences, showBenchmark } = config;
		const portfolioMetricPreferences: Array<PortfolioMetricPreferenceDTO> = preferences
			.map((x) => ({
				enabled: x.enabled,
				metricType: x.metricType,
			}))
			.filter((x) => x.enabled);

		const metrics = await axiosExtract(
			getApiGen(InvestmentReportsControllerApiFactory).getPortfolioMetrics(
				common.portfolio.uuid!,
				common.portfolio.primaryBenchmarkIdentifier!,
				{ portfolioMetricPreferences },
				common.asOf,
			),
		);
		return [
			jsxHelper(PortfolioMetrics, {
				commonProps: {
					showBenchmark,
				},
				splittableProps: (metrics.portfolioMetrics?.filter((x) => x.type) ?? []).map((x) => ({
					...x,
					unitOfMeasurement: unitOfMeasurement.portfolioMetric[x.type!] ?? "",
				})),
			}),
		];
	},
	async exAntePortfolioMetrics(config, common) {
		const metrics = await axiosExtract(
			common.variant === "current"
				? getApiGen(InvestmentReportsControllerApiFactory).getPortfolioExAnteMetrics(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
						common.asOf,
					)
				: getApiGen(InvestmentEnhancementReportsControllerApiFactory).getPortfolioExAnteMetrics1(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
					),
		);

		const { preferences, showBenchmark } = config;

		const riskConstraintTypes = new Set(["VAR", "VOLATILITY", "TRACKING_ERROR"]);

		const volatilityPreference = preferences.find((p) => p.metricType === "VOLATILITY");
		const varPreference = preferences.find((p) => p.metricType === "VAR");
		const trackingErrorPreference = preferences.find((p) => p.metricType === "TRACKING_ERROR");

		const userVolatilityMap = Map<PortfolioExAnteMetricTypeEnum, boolean>(
			volatilityList.map((metricType) => [metricType, volatilityPreference?.enabled ?? false]),
		);

		const userVarMap = Map<PortfolioExAnteMetricTypeEnum, boolean>(
			varList.map((metricType) => [metricType, varPreference?.enabled ?? false]),
		);

		const userTrackingErrorMap = Map<PortfolioExAnteMetricTypeEnum, boolean>(
			trackingErrorList.map((metricType) => [metricType, trackingErrorPreference?.enabled ?? false]),
		);

		const preferenceMap = Map<PortfolioExAnteMetricTypeEnum, boolean>(
			preferences
				.filter((p) => !riskConstraintTypes.has(p.metricType))
				.map((p) => [p.metricType as PortfolioExAnteMetricTypeEnum, p.enabled]),
		);

		const filteredExAnteMetrics = metrics.portfolioExAnteMetrics
			?.filter((x) => {
				if (x.type) {
					return x.fromUserConstraint
						? userVolatilityMap.get(x.type) || userVarMap.get(x.type) || userTrackingErrorMap.get(x.type)
						: preferenceMap.get(x.type);
				}
				return false;
			})
			.map((x) => ({
				...x,
				unitOfMeasurement: unitOfMeasurement.exAnteMetric[x.type!] ?? "",
				decimalPlaces: decimalPlaces.exAnteMetric[x.type!],
			}));

		const scorePreferencesByUuid = Map<string, boolean>(
			preferences
				.filter((p) => p.metricType === "SCORE_AVERAGE" && p.metricParameter?.classification?.classificationUuid)
				.map((p) => [p.metricParameter!.classification!.classificationUuid, p.enabled]),
		);

		const filteredScoreMetrics = (metrics.scorePortfolioExAnteMetrics ?? [])
			.filter((x) => {
				const uuid = x.metricParameters?.classificationUuid;
				return uuid != null && scorePreferencesByUuid.get(uuid) === true;
			})
			.map((x) => {
				const { unitOfMeasurement, scale } = classificationMetricDisplay(x.metadata?.classificationName);
				// Rates arrive as fractions; scale to match the "%" label (no-op scale of 1 otherwise).
				const scaled = (value: number | undefined) => (value == null ? value : value * scale);
				return {
					...x,
					current: scaled(x.current),
					benchmark: scaled(x.benchmark),
					proposal: scaled(x.proposal),
					unitOfMeasurement,
					decimalPlaces: 2 as const,
				};
			});

		return [
			jsxHelper(ExAnteMetrics, {
				commonProps: {
					benchmark: showBenchmark,
					variant: common.variant,
					proposalDate: common.portfolio.modificationDate,
				},
				splittableProps: [...(filteredExAnteMetrics ?? []), ...filteredScoreMetrics],
			}),
		];
	},
	async commentary(config, common) {
		const { commentaryTemplates } = common;
		const { useTemplateLanguage } = config;
		let reportLanguageToCommentaryLang = i18nLangToCommentaryLang[common.language];
		const commentaryResponse = await (async () => {
			try {
				let commentary: InvestmentCommentaryResponse | undefined;

				let sanitizedTemplateUuid = config.templateUuid;

				if (
					!sanitizedTemplateUuid ||
					(commentaryTemplates && !commentaryTemplates.find((x) => x.uuid === config.templateUuid))
				) {
					sanitizedTemplateUuid =
						commentaryTemplates.find((x) => x.name === "Standard Template")?.uuid ?? config.templateUuid;
				}

				commentary = await axiosExtract(
					common.variant === "current"
						? getApiGen(InvestmentReportsControllerApiFactory).getCommentaries(common.portfolio.uuid!, common.asOf)
						: getApiGen(InvestmentEnhancementReportsControllerApiFactory).getCommentaries1(common.portfolio.uuid!),
				);

				if (useTemplateLanguage) {
					reportLanguageToCommentaryLang =
						commentaryTemplates.find((x) => x.uuid === sanitizedTemplateUuid)?.language ??
						reportLanguageToCommentaryLang;
				}

				if (
					sanitizedTemplateUuid &&
					(commentary.template?.uuid !== sanitizedTemplateUuid ||
						commentary.status !== "COMPLETED" ||
						commentary.template.language !== reportLanguageToCommentaryLang)
				) {
					const detailedCommentary = await axiosExtract(
						getApiGen(CommentaryTemplateControllerApiFactory).preview1(
							common.portfolio.uuid!,
							sanitizedTemplateUuid,
							reportLanguageToCommentaryLang,
							common.variant === "proposal",
							common.asOf,
						),
					);
					commentary = {
						commentary: detailedCommentary,
						status: "COMPLETED",
						template: commentaryTemplates.find((x) => x.uuid === sanitizedTemplateUuid),
					};
				}

				return commentary;
			} catch (e) {
				console.error(e);
				throw e;
			}
		})().catch(() => undefined);

		return !commentaryResponse?.commentary
			? [
					jsxHelper(Commentary, {
						commonProps: {
							commentary: { status: "COMPLETED" },
							config,
						},
						splittableProps: getCommentaryProps({
							...commentaryResponse,
							commentary: i18next.getFixedT(common.language)("REPORT.COMMENTARY_SOON"),
						}),
					}),
				]
			: [
					jsxHelper(Commentary, {
						commonProps: {
							commentary: commentaryResponse,
							config,
						},
						splittableProps: getCommentaryProps({
							...commentaryResponse,
							commentary: commentaryResponse.commentary,
						}),
					}),
				];
	},
	async composition(config, common) {
		const instrumentsClassificationsControllerV1Api = getApiGen(InstrumentsClassificationsControllerV1ApiFactory);
		const userClassifications = await axiosExtract(
			instrumentsClassificationsControllerV1Api.retrieveAllClassifications(),
		);

		const { composition } = await axiosExtract(
			common.variant === "current"
				? getApiGen(InvestmentReportsControllerApiFactory).getInvestmentComposition(common.portfolio.uuid!, common.asOf)
				: getApiGen(InvestmentEnhancementReportsControllerApiFactory).getInvestmentComposition1(common.portfolio.uuid!),
		);

		const nonNullableComposition = composition ?? [];
		const userInstrumentsMap = await reviewTickerToInstrumentsMap(nonNullableComposition);
		const instruments = createInstrumentList({
			composition: nonNullableComposition,
			userInstrumentsMap,
		});

		return [
			jsxHelper(Composition, {
				commonProps: {
					variant: common.variant,
					portfolio: common.portfolio,
					userClassifications,
					config,
				},
				splittableProps: instruments?.sort(builtInSortFnFor("weight", "desc")) ?? [],
			}),
		];
	},
	async exposure(config, common) {
		switch (common.variant) {
			case "current": {
				const reportEnhanceApi = getApiGen(InvestmentReportsControllerApiFactory);
				const all = await parallelize(
					config.selectedCategories.map(({ value, type, label }) => async () => {
						const { investmentComposition: composition } = await axiosExtract(
							reportEnhanceApi.getTwoLevelsInvestmentExposure(
								common.portfolio.uuid!,
								common.portfolio.primaryBenchmarkIdentifier!,
								value,
								common.asOf,
							),
						);
						return !composition?.length
							? null
							: jsxHelper(Exposure, {
									commonProps: {
										comparison(t) {
											if (type === "RISK_MODEL") {
												return t(`REPORT_BUILDER.EXPOSURE.OPTIONS.${value}`);
											}

											return label;
										},
										composition: composition ?? [],
									},
									splittableProps: chunkExposureListItems(
										exposureEntriesToSplittableProps(composition ?? []),
										exposureChartHeight,
									),
									startsFromBlankPage: true,
								});
					}),
					{ concurrency: 4 },
				);
				return all.filter(Boolean) as ComponentAndPropsPair<any, unknown>[];
			}
			case "proposal": {
				const reportEnhanceApi = getApiGen(InvestmentEnhancementReportsControllerApiFactory);
				const all = await parallelize(
					config.selectedCategories.map(({ value, type, label }) => async () => {
						const { investmentComposition, enhancementComposition } = await axiosExtract(
							reportEnhanceApi.getTwoLevelsInvestmentExposure1(
								common.portfolio.uuid!,
								common.portfolio.primaryBenchmarkIdentifier!,
								value,
							),
						);
						return !investmentComposition?.length || !enhancementComposition?.length
							? null
							: jsxHelper(ExposureCompare, {
									commonProps: {
										comparison(t) {
											if (type === "RISK_MODEL") {
												return t(`REPORT_BUILDER.EXPOSURE.OPTIONS.${value}`);
											}

											return label;
										},
										composition: investmentComposition ?? [],
										proposalDate: common.portfolio.modificationDate,
									},
									splittableProps: exposureCompareEntriesToSplittableProps({
										currentComposition: investmentComposition ?? [],
										enhancedComposition: enhancementComposition ?? [],
									}),
									startsFromBlankPage: true,
								});
					}),
					{ concurrency: 4 },
				);
				return all.filter(Boolean) as ComponentAndPropsPair<any, unknown>[];
			}
		}
	},
	freeText(config, _common) {
		return [
			jsxHelper(FreeText, {
				commonProps: {
					config,
				},
				splittableProps: [],
			}),
		];
	},
	async performance(config, common) {
		const { benchmark, current } = await axiosExtract(
			getApiGen(InvestmentReportsControllerApiFactory).getInvestmentPerformance(
				common.portfolio.uuid!,
				common.portfolio.primaryBenchmarkIdentifier!,
				common.asOf,
			),
		);
		return !current
			? []
			: [
					jsxHelper(PortfolioPerformance, {
						commonProps: {
							current: current as [number, number][],
							benchmark: benchmark as [number, number][],
							showBenchmark: config.benchmark,
						},
						splittableProps: [],
					}),
				];
	},
	async performanceAttribution(config, common) {
		const { id: _id, kind: _kind, ...params } = config;

		const preferences = customObjectEntriesFn(params).flatMap(([k, v]) => (v ? [k] : []));
		const attributionResponses = await parallelize(
			preferences.map((horizon) => async () => ({
				data: await axiosExtract(
					getApiGen(InvestmentReportsControllerApiFactory).getRealizedPerformanceAttribution(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
						horizon,
						common.asOf,
					),
				),
				horizon,
			})),
		);

		return attributionResponses.flatMap(({ data, horizon }) => {
			if (!data.current?.length) {
				return [];
			}

			return [
				jsxHelper(PerformanceAttribution, {
					commonProps: {
						performanceAttribution: { current: data.current },
						horizon,
					},
					splittableProps: [],
					startsFromBlankPage: true,
				}),
			];
		});
	},
	async exAnteContributionVolatility(config, common) {
		const data = await axiosExtract(
			common.variant === "current"
				? getApiGen(InvestmentReportsControllerApiFactory).getCompositionVolatilityContribution(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
						"ONE_YEAR",
						common.asOf,
					)
				: getApiGen(InvestmentEnhancementReportsControllerApiFactory).getCompositionVolatilityContribution1(
						common.portfolio.uuid!,
						"ONE_YEAR",
					),
		);

		const map: Record<keyof (typeof config)["categories"], ExAnteVolatilityContributionSelectionProps> = {
			MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: {
				selector: "assetClass",
				vsSelector: "geography",
			},
			MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: {
				selector: "assetClass",
				vsSelector: "microAssetClass",
			},
			MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: {
				selector: "microAssetClass",
				vsSelector: "geography",
			},
			MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: {
				selector: "geography",
				vsSelector: "assetClass",
			},
			MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: {
				selector: "geography",
				vsSelector: "microAssetClass",
			},
		};
		return typedObjectEntries(config.categories)
			.filter(([, enabled]) => enabled)
			.flatMap(([selectorStr]) => {
				const selector = map[selectorStr];
				const dataVSdata = getExAnteVolatilityContributionSplittableProps(
					data,
					selector,
					common.variant,
					config.filterSignificant,
				);
				return !dataVSdata.list.length
					? []
					: [
							jsxHelper(ExAnteVolatilityContribution, {
								commonProps: {
									variant: common.variant,
									data,
									comparison: dataVSdata.opts,
									graphLimit: dataVSdata.graphLimit,
									graphColumnMarkers: dataVSdata.graphColumnMarkers,
									proposalDate: common.portfolio.modificationDate,
								},
								splittableProps: dataVSdata.list,
								startsFromBlankPage: true,
							}),
						];
			});
	},
	async performanceAndVolatilityContribution(config, common) {
		const [contributionVolatility, performanceVolatility] = await Promise.all([
			axiosExtract(
				getApiGen(InvestmentReportsControllerApiFactory).getRealizedVolatilityContribution(
					common.portfolio.uuid!,
					common.portfolio.primaryBenchmarkIdentifier!,
					"ONE_YEAR",
					common.asOf,
				),
			),
			axiosExtract(
				getApiGen(InvestmentReportsControllerApiFactory).getRealizedPerformanceContribution(
					common.portfolio.uuid!,
					common.portfolio.primaryBenchmarkIdentifier!,
					"ONE_YEAR",
					common.asOf,
				),
			),
		]);

		const map: Record<keyof (typeof config)["categories"], PerformanceContributionVolatilitySelectionProps> = {
			MACRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: {
				kind: "grouped",
				selector: "assetClass",
				vsSelector: "geography",
			},
			MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS: {
				kind: "grouped",
				selector: "assetClass",
				vsSelector: "microAssetClass",
			},
			MICRO_ASSET_CLASS_VS_MACRO_GEOGRAPHY: {
				kind: "grouped",
				selector: "microAssetClass",
				vsSelector: "geography",
			},
			MACRO_GEOGRAPHY_VS_MACRO_ASSET_CLASS: {
				kind: "grouped",
				selector: "geography",
				vsSelector: "assetClass",
			},
			MACRO_GEOGRAPHY_VS_MICRO_ASSET_CLASS: {
				kind: "grouped",
				selector: "geography",
				vsSelector: "microAssetClass",
			},
			COMPOSITION: { kind: "composition" },
		};
		return typedObjectEntries(config.categories)
			.filter(([, enabled]) => enabled)
			.flatMap(([selectorStr]) => {
				const selector = map[selectorStr];
				const data = { contributionVolatility, performanceVolatility };
				const dataVSdata = getPerformanceVolatilityContributionProps(data, selector, config.filterSignificant);
				return !dataVSdata.list.length
					? []
					: [
							jsxHelper(PerformanceVolatilityContribution, {
								commonProps: {
									data,
									comparison: dataVSdata.opts,
									graphLimit: dataVSdata.graphLimit,
									graphColumnMarkers: dataVSdata.graphColumnMarkers,
								},
								splittableProps: dataVSdata.list,
								startsFromBlankPage: true,
							}),
						];
			});
	},
	async factorExposure(config, common) {
		const investmentFactors = await axiosExtract(
			common.variant === "current"
				? getApiGen(InvestmentReportsControllerApiFactory).getInvestmentFactors(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
						common.asOf,
					)
				: getApiGen(InvestmentEnhancementReportsControllerApiFactory).getInvestmentFactors1(
						common.portfolio.uuid!,
						common.portfolio.primaryBenchmarkIdentifier!,
					),
		);
		const data = { investmentFactors, variant: common.variant };
		const exposureFactorsData = getExposureFactorsProps(data, config.filterSignificant);

		return !exposureFactorsData.list.length
			? []
			: [
					jsxHelper(ExposureFactors, {
						commonProps: {
							proposalDate: common.portfolio.modificationDate,
							variant: common.variant,
							data,
							graphLimits: exposureFactorsData.graphLimits,
							graphColumnMarkers: exposureFactorsData.graphColumnMarkers,
							config,
						},
						splittableProps: exposureFactorsData.list,
						startsFromBlankPage: true,
					}),
				];
	},
	async bondsDividendsTracker(config, common) {
		// The report is not paginated: fetch a single page of up to `maxItems` events per enabled section.
		const sections = [
			{
				title: "Bonds & Dividends — History",
				cfg: config.history,
				tab: "HISTORY" as const,
			},
			{
				title: "Bonds & Dividends — Upcoming",
				cfg: config.upcoming,
				tab: "UPCOMING" as const,
			},
		].filter(({ cfg }) => cfg.enabled);

		const result: ComponentAndPropsPair<{ sectionTitle: string }, BondDividendRow>[] = [];
		for (const { title, cfg, tab } of sections) {
			const page = await portfolioRepository.bondCashFlows.fetchQuery({
				uuid: common.portfolio.uuid ?? "",
				tab,
				offset: 0,
				limit: cfg.maxItems,
				...(common.asOf ? { asOf: common.asOf } : {}),
			});
			result.push(
				jsxHelper(BondsDividendsTracker, {
					commonProps: { sectionTitle: title },
					splittableProps: (page.items ?? []).map(toBondDividendRow),
				}),
			);
		}
		return result;
	},
	async maturityCouponTracker(config, common) {
		// Each section is shown or hidden as a whole; every bucket the endpoint returns is rendered for
		// an enabled section (per-bucket toggles are not offered — see the report-v13 config).
		// The € readings of a portfolio in quantities are not in the printout.
		const result: ComponentAndPropsPair<
			{ sectionTitle: string; section: "maturity" | "coupon" },
			MaturityCouponPrintRow
		>[] = [];

		// Called with one argument when live: an extra opts object appends an element to the query
		// key, which would change the live view's cache entry.
		const uuid = common.portfolio.uuid ?? "";
		const tracker = await (common.asOf
			? portfolioRepository.maturityCouponTracker.fetchQuery(uuid, { asOf: common.asOf })
			: portfolioRepository.maturityCouponTracker.fetchQuery(uuid));

		if (config.maturity.enabled) {
			const rows: MaturityCouponPrintRow[] = toMaturityGroups(tracker).flatMap((group) =>
				group.rows.map((row) => ({
					bucketShortName: group.shortName,
					instrumentName: row.instrumentName,
					tickerName: row.tickerName,
					date: row.expirationDate,
				})),
			);
			result.push(
				jsxHelper(MaturityCouponTracker, {
					commonProps: { sectionTitle: "Maturity date", section: "maturity" },
					splittableProps: rows,
				}),
			);
		}

		if (config.coupon.enabled) {
			const rows: MaturityCouponPrintRow[] = toCouponGroups(tracker).flatMap((group) =>
				group.rows.map((row) => ({
					bucketShortName: group.shortName,
					instrumentName: row.instrumentName,
					tickerName: row.tickerName,
					date: row.couponDate,
					couponRate: row.couponRate,
				})),
			);
			result.push(
				jsxHelper(MaturityCouponTracker, {
					commonProps: { sectionTitle: "Coupon date", section: "coupon" },
					splittableProps: rows,
				}),
			);
		}

		return result;
	},
	disclaimer(config) {
		return [
			jsxHelper(Disclaimer, {
				commonProps: {
					disclaimer: config.content.enabled ? config.content : undefined,
					address: undefined,
					mail: undefined,
					website: undefined,
					contacts: config.contact.enabled ? config.contact.description : undefined,
				},
				splittableProps: [],
				startsFromBlankPage: true,
				hideFooter: true,
				hideHeader: true,
			}),
		];
	},
} as const satisfies {
	[K in keyof ReportTemplateItemMap]: (
		config: ReportTemplateItemFor<K>,
		common: CommonPortfolioData,
		index: number,
		user: IUser,
	) => MaybePromise<Array<ComponentAndPropsPair<any, unknown>>>;
};
